import React, {useEffect, useRef, useState} from "react";

import {PreFetchDataProvider} from "@/providers/PreFetchDataProvider.jsx";
import {
    POSTING_ENDPOINT,
    POSTING_LOG_ENDPOINT,
    POSTING_PREFETCH_DATA_ENDPOINT,
    POSTING_VALIDATE_ENDPOINT
} from "@/constants/endpoints.jsx";
import {usePreFetchData} from "@/hooks/usePreFetchData.jsx";
import JobDetails from "@/views/posting/JobDetails.jsx";
import {BrowserRouter, Route, Routes, useLocation, Navigate as RouterNavigate} from "react-router-dom";
import {NAVIGATION_ANIMATION_DURATION} from "@/constants/navigation.jsx";
import {LocalStorageProvider} from "@/providers/LocalStorageProvider.jsx";
import {FormProgressProvider} from "@/providers/FormProgressProvider.jsx";
import FormProgress from "@/components/form/FormProgress.jsx";
import RouterContainer from "@/components/RouterContainer.jsx";
import {CSSTransition, TransitionGroup} from "react-transition-group";
import FormProgressProtectRoute from "@/components/FormProgressProtectRoute.jsx";
import {PASSWORD_RESET, POST_JOB, SIGN_IN} from "@/constants/routes/index.jsx";
import {
    EMPLOYER_ADD_A_PAYMENT_METHOD,
    EMPLOYER_COMPLETE_PROFILE,
    EMPLOYER_INFORMATION,
} from "@/constants/routes/sign-up.jsx";
import EmployerPaymentMethod from "@/views/sign-up/employer/EmployerPaymentMethod.jsx";
import EmployerProfile from "@/views/sign-up/employer/EmployerProfile.jsx";
import EmployerInformation from "@/views/sign-up/employer/EmployerInformation.jsx";
import {useFormProgress} from "@/hooks/useFormProgress.jsx";
import {useLocalStorage} from "@/hooks/useLocalStorage.jsx";
import {useForm} from "@/hooks/useForm.jsx";
import ContainerBox from "@/components/ContainerBox.jsx";
import {ContainerBoxHeader, ContainerBoxTitle} from "@/components/typography/index.jsx";
import SignIn from "@/views/auth/SignIn.jsx";
import {sendConversion} from "@/utils/sendConversion.jsx";

export default function Posting() {
    const allowedSaveToStackRoutes = [
        '/',
        EMPLOYER_INFORMATION,
        EMPLOYER_COMPLETE_PROFILE,
        EMPLOYER_ADD_A_PAYMENT_METHOD,
    ];

    const flows = [
        [
            EMPLOYER_INFORMATION,
            EMPLOYER_COMPLETE_PROFILE,
            EMPLOYER_ADD_A_PAYMENT_METHOD,
        ],
    ];

    return (
        <BrowserRouter basename={POST_JOB}>
            <PreFetchDataProvider endpoint={POSTING_PREFETCH_DATA_ENDPOINT}>
                <LocalStorageProvider storageKey={'posting'}>
                    <FormProgressProvider openedRoutes={['/']} initialStepsCount={3} allowedSaveToStackRoutes={allowedSaveToStackRoutes} flows={flows} progressLine={true} progressLineStartStep={2}>
                        <PostingRouter/>
                    </FormProgressProvider>
                </LocalStorageProvider>
            </PreFetchDataProvider>
        </BrowserRouter>
    );
};

const PostingRouter = function () {
    const {
        callEndpoint: callPostingEndpoint,
        state: postingState,
    } = useForm({endpoint: POSTING_ENDPOINT, defaultValues: {}});

    const {
        callEndpoint: callPostingLogEndpoint,
    } = useForm({endpoint: POSTING_LOG_ENDPOINT, defaultValues: {}});


    const {preFetchedData, reFetchPreData} = usePreFetchData();
    const {navigate, stepsCount, currentProgress, setStepsCount, setCurrentProgress} = useFormProgress();
    const {resetStoredData} = useLocalStorage();
    const location = useLocation();

    const [stepName, setStepName] = useState('Post a Job');
    const [postingTriggerSubmit, setPostingTriggerSubmit] = useState(false);

    const postingData = useRef({});
    const currentProgressRef = useRef({});
    const maxProgressRef = useRef(0);

    const onPostingSubmitCallback = (endpointResponse, data) => {
        resetStoredData();
        if (endpointResponse?.data?.redirect) {
            window.location = endpointResponse.data.redirect;
        }
    }

    const onPostingChangeCallback = () => {
        callPostingLogEndpoint(postingData.current, false, (endpointResponse) => {
        }).then((result) => {});
    }

    const onFinishRegistrationCallback = () => {
        callPostingEndpoint(postingData.current, false, (endpointResponse) => {
            if (endpointResponse?.data?.isNotHasSubscription) {
                reFetchPreData();
                navigate(EMPLOYER_ADD_A_PAYMENT_METHOD);
            } else {
                onPostingSubmitCallback(endpointResponse, postingData.current)
            }
        }).then((result) => {
            if (!result.success) {
                if (Object.keys(result.errors ?? {}).length > 0) {
                    setPostingTriggerSubmit(true);
                } else {
                    reFetchPreData();
                }
                navigate('/');
            }
        });
    }

    const onFinishPaymentCallback = () => {
        try {
            sendConversion(16810486703, 'cpP7CN30-fMZEK_X7s8-', preFetchedData?.posting_subscription_price ?? 0);
        } catch (e) {}

        onFinishRegistrationCallback();
    }

    const defaultRoute = {
        path: '/', element: <JobDetails
            currentProgress={0}
            endpoint={POSTING_ENDPOINT}
            onSubmitCallback={onPostingSubmitCallback}
            buttonText={'Post'}
        />
    };

    const [postingRoutes, setPostingRoutes] = useState([defaultRoute]);

    useEffect(() => {
        if (location.pathname == '/') {
            setStepName('Post a Job');
        } else if (location.pathname == EMPLOYER_INFORMATION) {
            setStepName('Post a Job - Create an Account');
        } else if (location.pathname == EMPLOYER_COMPLETE_PROFILE) {
            setStepName('Post a Job - Additional Details');
        } else if (location.pathname == EMPLOYER_ADD_A_PAYMENT_METHOD) {
            setStepName('Post a Job - Payment Details');
        } else if (location.pathname == SIGN_IN) {
            setStepName('Log in to Finish Posting');
            setCurrentProgress(0);
        } else {
            setStepName('Payment Details');
        }
    }, [location.pathname]);

    useEffect(() => {
        if(maxProgressRef.current <=1 && (!currentProgressRef.current || currentProgress < currentProgressRef.current)) {
            if (preFetchedData?.isAuth && !preFetchedData?.isHasSubscription) {
                setStepsCount(2);
            } else {
                setStepsCount(3);
            }
        }

        currentProgressRef.current = currentProgress;

        if(currentProgress > 0) {
            onPostingChangeCallback();
        }

        currentProgressRef.current = currentProgress;

        if(maxProgressRef.current < currentProgress) {
            maxProgressRef.current = currentProgress;
        }
    }, [preFetchedData, currentProgress]);

    useEffect(() => {
        // if ((preFetchedData?.isAuth && (preFetchedData?.isHasSubscription)) || !preFetchedData?.isCanPost) {
        if (preFetchedData?.isAuth && preFetchedData?.isHasSubscription) {
            setPostingRoutes([defaultRoute]);
        } else if (stepsCount === 2 /*preFetchedData?.isAuth && !preFetchedData?.isHasSubscription*/) {
            setPostingRoutes([
                {
                    path: '/',
                    element: <JobDetails endpoint={POSTING_VALIDATE_ENDPOINT} triggerSubmit={postingTriggerSubmit}
                                         onSubmitCallback={(endpointResponse, data) => {
                                             postingData.current = data;
                                             setPostingTriggerSubmit(false);
                                             navigate(EMPLOYER_COMPLETE_PROFILE);
                                         }}/>
                },
                {
                    path: EMPLOYER_COMPLETE_PROFILE,
                    element: <EmployerProfile currentProgress={stepsCount - 1}/>
                },
                {path: EMPLOYER_ADD_A_PAYMENT_METHOD, element: <EmployerPaymentMethod currentProgress={stepsCount} buttonText={'Post'} onSubmitCallback={onFinishPaymentCallback}/>},
            ]);
        } else {
            setPostingRoutes([
                {
                    path: '/',
                    element: <JobDetails endpoint={POSTING_VALIDATE_ENDPOINT} triggerSubmit={postingTriggerSubmit}
                                         onSubmitCallback={(endpointResponse, data) => {
                                             postingData.current = data;
                                             setPostingTriggerSubmit(false);
                                             navigate(EMPLOYER_INFORMATION);
                                         }}/>
                },
                {
                    path: SIGN_IN, element:
                        <SignInToPost
                            onSubmitCallback={onFinishRegistrationCallback}
                            onCreateAccountClick={() => {
                                navigate(EMPLOYER_INFORMATION)
                            }}/>
                },
                {
                    path: EMPLOYER_INFORMATION, element: <>
                        <EmployerInformation currentProgress={stepsCount - 2}/>
                        <div className="mt-5 text-center">
                            <p className="pb-2 fs-4 fw-semibold">Already have an account?</p>
                            <div className="fs-4 fw-bold color-link" onClick={() => {
                                navigate(SIGN_IN)
                            }} role="button">Log in →
                            </div>
                        </div>
                    </>
                },
                {
                    path: EMPLOYER_COMPLETE_PROFILE,
                    element: <EmployerProfile currentProgress={stepsCount - 1}/>
                },
                {path: EMPLOYER_ADD_A_PAYMENT_METHOD, element: <EmployerPaymentMethod currentProgress={stepsCount} buttonText={'Post'} onSubmitCallback={onFinishPaymentCallback}/>},
            ]);
        }
    }, [preFetchedData, stepsCount, postingTriggerSubmit]);

    return (
        <>
            <FormProgress>{stepName}</FormProgress>
            <RouterContainer>
                <TransitionGroup
                    component={null}
                >
                    <CSSTransition
                        key={location.key}
                        timeout={NAVIGATION_ANIMATION_DURATION}
                        classNames={'crossfade'}
                    >
                        <Routes location={location}>
                            {postingRoutes.map((route) => (
                                <Route
                                    key={route.path}
                                    path={route.path}
                                    element={<FormProgressProtectRoute
                                        path={route.path}>{route.element}</FormProgressProtectRoute>}
                                />
                            ))}
                            <Route path="*" element={<RouterNavigate to={'/'} replace/>}/>
                        </Routes>
                    </CSSTransition>
                </TransitionGroup>
            </RouterContainer>
        </>
    );
}

const SignInToPost = ({onSubmitCallback, onCreateAccountClick}) => {
    return (
        <>
            <ContainerBox>
                <ContainerBoxHeader>
                    <ContainerBoxTitle>Log in</ContainerBoxTitle>
                </ContainerBoxHeader>
                <div style={{width: '450px'}}>
                    <SignIn onSubmitCallback={onSubmitCallback}/>
                </div>
                <div className="mt-4 mb-3">
                    <a href={PASSWORD_RESET} target="_blank" className="fs-5 fw-bold">Forgot password? Click here</a>
                </div>
            </ContainerBox>
            <div className="mt-5 text-center">
                <p className="pb-2 fs-4 fw-semibold">Don’t have an account?</p>
                <div className="fs-4 fw-bold color-link" onClick={onCreateAccountClick} role="button">Create an Account
                    →
                </div>
            </div>
        </>
    )
}
